import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../context/AuthContext';
import { useAppState } from '../../context/AppStateContext';
import LogDisciplineModal from '../../components/discipline/LogDisciplineModal';
import ScholarStandingRoster from '../../components/discipline/ScholarStandingRoster';
import ClassHouseReportModal from '../../components/discipline/ClassHouseReportModal';
import FilterDropdown from '../../components/common/FilterDropdown';
import PremiumTooltip from '../../components/common/PremiumTooltip';
import {
    TrophyIcon,
    ExclamationTriangleIcon,
    PlusIcon,
    MagnifyingGlassIcon,
    ScaleIcon,
    ArrowPathIcon,
    ShieldExclamationIcon,
    XMarkIcon,
    CalendarIcon,
    UserCircleIcon,
    FunnelIcon,
    ArrowLeftIcon,
    ArrowDownTrayIcon,
    ChevronDownIcon,
    CheckCircleIcon
} from '@heroicons/react/24/outline';

// Teacher Discipline Manager - Handles Merits, Demerits, and Character Roster
const TeacherDisciplineManager = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { get, post } = useApi();
    const { user } = useAuth();
    const { showToast, is_hod, is_class_teacher, is_head_of_studies, is_principal } = useAppState();
    const queryClient = useQueryClient();

    // Check location state for initial tab/filter
    const initialTab = location.state?.tab || 'merits';
    const initialStatus = location.state?.filter === 'escalated' ? 'escalated' : 'all';

    // Active Tab: 'merits' | 'demerits' | 'roster'
    const [activeTab, setActiveTab] = useState(initialTab);
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    // Modals & Inline Dropdown states
    const [isLogModalOpen, setIsLogModalOpen] = useState(false);
    const [openActionMenuId, setOpenActionMenuId] = useState(null);
    const [escalatingRecord, setEscalatingRecord] = useState(null);
    const [escalationOwners, setEscalationOwners] = useState([]);
    const [escalationNote, setEscalationNote] = useState('');
    const [resolvingRecord, setResolvingRecord] = useState(null);
    const [resolutionNote, setResolutionNote] = useState('');
    const [voidingRecord, setVoidingRecord] = useState(null);
    const [voidReason, setVoidReason] = useState('');

    // Filters
    const [selectedCourseFilter, setSelectedCourseFilter] = useState('all');
    const [selectedYear, setSelectedYear] = useState('');
    const [selectedTerm, setSelectedTerm] = useState('');
    const [filterSeverity, setFilterSeverity] = useState('all');
    const [filterStatus, setFilterStatus] = useState(initialStatus);
    const [rosterStandingFilter, setRosterStandingFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    // Close action menu when clicking outside
    React.useEffect(() => {
        const handleOutsideClick = (e) => {
            if (!e.target.closest('.action-menu-container')) {
                setOpenActionMenuId(null);
            }
        };
        window.addEventListener('click', handleOutsideClick);
        return () => window.removeEventListener('click', handleOutsideClick);
    }, []);

    // Safe date formatting helper
    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return '—';
            return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
        } catch {
            return '—';
        }
    };

    // Fetch Academic Years for Year Filter
    const { data: years = [] } = useQuery({
        queryKey: ['academic-years'],
        queryFn: () => get('/api/academic-years/'),
        staleTime: 60 * 60 * 1000,
    });

    // Fetch Academic Terms for Term Filter
    const { data: terms = [] } = useQuery({
        queryKey: ['academic-terms'],
        queryFn: () => get('/api/academic-terms/'),
        staleTime: 60 * 60 * 1000,
    });

    const hasInitializedTerm = React.useRef(false);
    React.useEffect(() => {
        const rawTerms = Array.isArray(terms) ? terms : (terms?.results || []);
        if (!hasInitializedTerm.current && rawTerms.length > 0) {
            const current = rawTerms.find(t => t.is_current);
            if (current) {
                setSelectedTerm(String(current.id));
                if (current.year) {
                    setSelectedYear(String(current.year));
                }
            }
            hasInitializedTerm.current = true;
        }
    }, [terms]);

    // 1. Fetch Teacher's Courses for Class Selector
    const { data: coursesData } = useQuery({
        queryKey: ['teacher-courses-options'],
        queryFn: () => get('/teachers/api/courses/'),
        staleTime: 5 * 60 * 1000,
    });

    // Fetch HOD's Managed School Levels (Grades & Streams)
    const { data: myLevels = [] } = useQuery({
        queryKey: ['hod-managed-school-levels'],
        queryFn: () => get('/api/cbc/school-levels/my_levels/'),
        enabled: !!(is_hod || is_head_of_studies || is_principal),
        staleTime: 10 * 60 * 1000,
    });

    const teacherCourses = useMemo(() => {
        return coursesData?.courses || (Array.isArray(coursesData) ? coursesData : []);
    }, [coursesData]);

    const courseOptions = useMemo(() => {
        const isHodUser = is_hod || is_head_of_studies || is_principal;
        const list = [
            { name: isHodUser ? 'All Department Classes' : 'All My Classes & Streams', value: 'all' }
        ];

        // If HOD, add department streams and grades
        if (isHodUser && Array.isArray(myLevels)) {
            myLevels.forEach(lvl => {
                (lvl.grades || []).forEach(grd => {
                    if (grd.streams && grd.streams.length > 0) {
                        grd.streams.forEach(st => {
                            const val = `stream_${st.id}`;
                            if (!list.some(item => item.value === val)) {
                                list.push({
                                    name: `Stream: ${grd.name} (${st.name})`,
                                    value: val
                                });
                            }
                        });
                    } else {
                        const val = `grade_${grd.id}`;
                        if (!list.some(item => item.value === val)) {
                            list.push({
                                name: `Grade: ${grd.name}`,
                                value: val
                            });
                        }
                    }
                });
            });
        }

        // Add managed streams if class teacher (omits teacher personal name)
        const assignedGrades = coursesData?.assigned_grade || [];
        const managedStreams = assignedGrades.filter(g => g.is_stream);
        if (managedStreams.length > 0) {
            managedStreams.forEach(s => {
                const val = `stream_${s.stream_id}`;
                if (!list.some(item => item.value === val)) {
                    list.push({
                        name: `Stream: ${s.name} (Class Teacher)`,
                        value: val
                    });
                }
            });
        }

        // Add taught courses
        teacherCourses.forEach(c => {
            const val = `course_${c.id}`;
            if (!list.some(item => item.value === val)) {
                list.push({
                    name: `${c.name} ${c.grade_level_name ? `(${c.grade_level_name} ${c.stream_display_name || ''})` : ''}`.trim(),
                    value: val
                });
            }
        });

        return list;
    }, [is_hod, is_head_of_studies, is_principal, myLevels, coursesData, teacherCourses]);

    const yearOptions = useMemo(() => [
        { name: 'All Academic Years', value: '' },
        ...years.map(y => ({
            name: `${y.name} ${y.is_current ? '(Current Year)' : ''}`.trim(),
            value: String(y.id)
        }))
    ], [years]);

    const termOptions = useMemo(() => {
        const filteredTerms = selectedYear
            ? terms.filter(t => String(t.year) === String(selectedYear))
            : terms;
        return [
            { name: 'All Terms', value: '' },
            ...filteredTerms.map(t => ({
                name: `${t.name} ${t.year_name ? `(${t.year_name})` : ''} ${t.is_current ? '• Current' : ''}`.trim(),
                value: String(t.id)
            }))
        ];
    }, [terms, selectedYear]);

    const standingOptions = useMemo(() => [
        { name: 'All Standings', value: 'all' },
        { name: 'Exemplary', value: 'exemplary' },
        { name: 'Good Standing', value: 'good' },
        { name: 'Detention', value: 'detention' },
        { name: 'Suspension', value: 'suspension' },
        { name: 'Expelled', value: 'expelled' },
    ], []);

    // 2. Fetch Discipline Records with Role-Aware Scoping
    const {
        data: records = [],
        isLoading: loadingRecords
    } = useQuery({
        queryKey: ['teacher-discipline-records', user?.id, selectedCourseFilter, selectedYear, selectedTerm, is_hod, is_class_teacher],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (selectedCourseFilter !== 'all') {
                if (selectedCourseFilter.startsWith('stream_')) {
                    params.append('stream_id', selectedCourseFilter.replace('stream_', ''));
                } else {
                    params.append('course_id', selectedCourseFilter.replace('course_', ''));
                }
            }
            if (selectedYear) params.append('year_id', selectedYear);
            if (selectedTerm) params.append('term_id', selectedTerm);

            if (!is_hod && !is_class_teacher && !is_head_of_studies && !is_principal) {
                params.append('logged_by_me', 'true');
            }

            const res = await get(`/api/discipline/records/?${params.toString()}`);
            return Array.isArray(res) ? res : [];
        },
        staleTime: 30 * 1000,
    });

    // 3. Fetch HOD Action & Escalation Queue Count (if HOD)
    const { data: hodQueueData = [] } = useQuery({
        queryKey: ['hod-discipline-queue'],
        queryFn: () => get('/api/discipline/records/hod-queue/'),
        enabled: !!is_hod,
        staleTime: 30 * 1000,
    });

    const pendingQueueCount = useMemo(() => {
        return hodQueueData.filter(r => ['pending_review', 'under_action', 'escalated'].includes(r.status)).length;
    }, [hodQueueData]);

    // 4. Fetch Staff for Escalation Modal
    const { data: staffList = [] } = useQuery({
        queryKey: ['discipline-staff-options'],
        queryFn: () => get('/api/discipline/records/staff-options/'),
        enabled: !!escalatingRecord,
        staleTime: 5 * 60 * 1000,
    });

    // Acknowledge Mutation (Mark Under Action)
    const acknowledgeMutation = useMutation({
        mutationFn: (recordId) => post(`/api/discipline/records/${recordId}/acknowledge/`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['teacher-discipline-records'] });
            queryClient.invalidateQueries({ queryKey: ['hod-discipline-queue'] });
            showToast?.('Incident acknowledged and marked Under Action.', 'success');
        },
        onError: (err) => {
            showToast?.(err.message || 'Failed to acknowledge incident', 'error');
        }
    });

    // Escalate Mutation
    const escalateMutation = useMutation({
        mutationFn: ({ recordId, payload }) => post(`/api/discipline/records/${recordId}/escalate/`, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['teacher-discipline-records'] });
            queryClient.invalidateQueries({ queryKey: ['hod-discipline-queue'] });
            showToast?.('Incident escalated successfully to designated senior staff.', 'success');
            setEscalatingRecord(null);
            setEscalationOwners([]);
            setEscalationNote('');
        },
        onError: (err) => {
            showToast?.(err.message || 'Failed to escalate incident', 'error');
        }
    });

    // Resolve Mutation
    const resolveMutation = useMutation({
        mutationFn: ({ recordId, note }) => post(`/api/discipline/records/${recordId}/resolve/`, { resolution_note: note }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['teacher-discipline-records'] });
            queryClient.invalidateQueries({ queryKey: ['hod-discipline-queue'] });
            showToast?.('Case resolved and closed. Parent notification dispatched.', 'success');
            setResolvingRecord(null);
            setResolutionNote('');
        },
        onError: (err) => {
            showToast?.(err.message || 'Failed to resolve case', 'error');
        }
    });

    // Void Mutation (HOD / Admin only)
    const voidMutation = useMutation({
        mutationFn: ({ recordId, reason }) => post(`/api/discipline/records/${recordId}/void/`, { void_reason: reason }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['teacher-discipline-records'] });
            queryClient.invalidateQueries({ queryKey: ['hod-discipline-queue'] });
            showToast?.('Record voided from active tallies and audit logged.', 'success');
            setVoidingRecord(null);
            setVoidReason('');
        },
        onError: (err) => {
            showToast?.(err.message || 'Failed to void record', 'error');
        }
    });

    const handleEscalationOwnerToggle = (ownerId) => {
        setEscalationOwners(prev => {
            const exists = prev.includes(ownerId);
            return exists ? prev.filter(id => id !== ownerId) : [...prev, ownerId];
        });
    };

    const handleEscalateSubmit = (e) => {
        e.preventDefault();
        if (escalationOwners.length === 0) {
            showToast?.('Please assign at least one follow-up owner for escalation', 'error');
            return;
        }
        escalateMutation.mutate({
            recordId: escalatingRecord.id,
            payload: {
                owner_ids: escalationOwners,
                note: escalationNote.trim() || 'Escalated by Class Teacher'
            }
        });
    };

    const handleResolveSubmit = (e) => {
        e.preventDefault();
        if (!resolutionNote.trim()) {
            showToast?.('A non-empty resolution note is required to close this case', 'error');
            return;
        }
        resolveMutation.mutate({
            recordId: resolvingRecord.id,
            note: resolutionNote.trim()
        });
    };

    const handleVoidSubmit = (e) => {
        e.preventDefault();
        if (!voidReason.trim()) {
            showToast?.('A reason is required to void this record', 'error');
            return;
        }
        voidMutation.mutate({
            recordId: voidingRecord.id,
            reason: voidReason.trim()
        });
    };

    // Filtered records
    const meritRecords = useMemo(() => {
        return records.filter(r => r.category_type === 'merit');
    }, [records]);

    const demeritRecords = useMemo(() => {
        return records.filter(r => r.category_type === 'demerit');
    }, [records]);

    const filteredMeritRecords = useMemo(() => {
        return meritRecords.filter(rec => {
            if (searchQuery) {
                const q = searchQuery.toLowerCase();
                const matchName = rec.student_name?.toLowerCase().includes(q);
                const matchAdm = (rec.student_id_number || rec.student_adm)?.toLowerCase().includes(q);
                const matchTitle = rec.title?.toLowerCase().includes(q);
                const matchCat = rec.category_name?.toLowerCase().includes(q);
                const matchDesc = rec.description?.toLowerCase().includes(q);
                if (!matchName && !matchAdm && !matchTitle && !matchCat && !matchDesc) return false;
            }
            return true;
        });
    }, [meritRecords, searchQuery]);

    const filteredDemeritRecords = useMemo(() => {
        return demeritRecords.filter(rec => {
            if (filterSeverity !== 'all' && rec.severity !== filterSeverity) return false;
            if (filterStatus !== 'all' && rec.status !== filterStatus) return false;
            if (searchQuery) {
                const q = searchQuery.toLowerCase();
                const matchName = rec.student_name?.toLowerCase().includes(q);
                const matchAdm = (rec.student_id_number || rec.student_adm)?.toLowerCase().includes(q);
                const matchCat = rec.category_name?.toLowerCase().includes(q);
                const matchDesc = rec.description?.toLowerCase().includes(q);
                const matchConseq = rec.consequences?.toLowerCase().includes(q);
                if (!matchName && !matchAdm && !matchCat && !matchDesc && !matchConseq) return false;
            }
            return true;
        });
    }, [demeritRecords, filterSeverity, filterStatus, searchQuery]);

    // Metric tallies
    const totalLogged = records.length;
    const totalMeritPoints = meritRecords.reduce((sum, r) => sum + (r.points || 0), 0);
    const totalDemeritPoints = demeritRecords.reduce((sum, r) => sum + (r.points || 0), 0);
    const pendingReviewCount = demeritRecords.filter(r => r.status === 'pending_review').length;
    const escalatedOrHodCount = useMemo(() => {
        if (is_hod) {
            return pendingQueueCount > 0 ? pendingQueueCount : demeritRecords.filter(r => r.status === 'escalated').length;
        }
        return demeritRecords.filter(r => r.status === 'escalated').length;
    }, [is_hod, pendingQueueCount, demeritRecords]);

    return (
        <div className="min-h-screen bg-[#F8FAFC] p-4 lg:p-8 space-y-8">
            <div className="max-w-7xl mx-auto space-y-8">

                {/* Rich Header with Access Level indicator */}
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
                    <div className="space-y-2 max-w-xl">
                        <div className="flex flex-wrap items-center gap-2">
                            {(is_hod || is_head_of_studies || is_principal) && (
                                <button
                                    type="button"
                                    onClick={() => navigate('/teacher/hod-dashboard')}
                                    className="inline-flex items-center gap-1.5 px-3 py-1 bg-white hover:bg-purple-50 text-purple-700 text-[10px] font-black uppercase tracking-wider rounded-full border border-purple-200 transition-colors shadow-sm"
                                >
                                    <ArrowLeftIcon className="w-3 h-3 text-purple-600" />
                                    HOD Dashboard
                                </button>
                            )}
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FFC425]/10 text-[#18216D] text-[10px] font-black uppercase tracking-[0.2em] rounded-full border border-[#FFC425]/20">
                                <ScaleIcon className="w-3 h-3 text-[#FFC425]" />
                                Kianda Character Registry
                            </div>
                        </div>
                        <h1 className="text-3xl sm:text-4xl font-black text-[#18216D] uppercase tracking-tighter">
                            Character <span className="text-[#3B82F6]">Registry</span>
                        </h1>
                        <p className="text-slate-500 font-medium italic text-sm sm:text-base">
                            Track behavioral infractions, reward student character, and oversee class-wide merit standings.
                        </p>
                    </div>

                    <div className="flex flex-col items-start md:items-end gap-3 w-full md:w-auto shrink-0">
                        <div className="flex items-center gap-3 flex-wrap md:justify-end">
                            {(is_hod || is_class_teacher || is_head_of_studies || is_principal) && (
                                <button
                                    onClick={() => setIsReportModalOpen(true)}
                                    className="flex items-center gap-2.5 px-6 py-3.5 bg-white text-[#18216D] border-2 border-[#18216D]/15 font-black rounded-2xl hover:border-[#18216D] shadow-sm active:scale-95 transition-all uppercase tracking-widest text-[11px]"
                                >
                                    <ArrowDownTrayIcon className="w-5 h-5 stroke-[2.5]" />
                                    Export Report
                                </button>
                            )}
                            <button
                                onClick={() => setIsLogModalOpen(true)}
                                className="flex items-center gap-2.5 px-6 py-3.5 bg-[#18216D] text-white font-black rounded-2xl hover:bg-[#0D164F] shadow-xl shadow-indigo-900/20 active:scale-95 transition-all uppercase tracking-widest text-[11px]"
                            >
                                <PlusIcon className="w-5 h-5 stroke-[3]" />
                                Log Merit / Demerit
                            </button>
                        </div>

                        {/* Search input placed below button */}
                        <div className="relative group w-full md:w-80">
                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#18216D] transition-colors">
                                <MagnifyingGlassIcon className="w-4 h-4" />
                            </div>
                            <input
                                type="text"
                                placeholder="Search incidents or scholars..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-11 pr-4 py-2.5 bg-white border border-slate-200 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#18216D]/10 focus:border-[#18216D] transition-all text-xs font-bold text-[#18216D]"
                            />
                        </div>
                    </div>
                </div>

                {/* Metrics Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Total Recorded</p>
                        <p className="text-2xl font-black text-[#18216D] mt-1">{totalLogged}</p>
                        <p className="text-[10px] font-bold text-slate-400 mt-1">Across assigned classes</p>
                    </div>
                    <div className="bg-white rounded-2xl border border-emerald-100/70 p-5 shadow-sm">
                        <p className="text-[10px] font-black uppercase tracking-widest text-emerald-600">Merits Awarded</p>
                        <p className="text-2xl font-black text-emerald-600 mt-1">+{totalMeritPoints}</p>
                        <p className="text-[10px] font-bold text-emerald-600/80 mt-1">{meritRecords.length} commendations</p>
                    </div>
                    <div className="bg-white rounded-2xl border border-rose-100/70 p-5 shadow-sm">
                        <p className="text-[10px] font-black uppercase tracking-widest text-rose-600">Demerits Logged</p>
                        <p className="text-2xl font-black text-rose-600 mt-1">-{totalDemeritPoints}</p>
                        <p className="text-[10px] font-bold text-rose-600/80 mt-1">{demeritRecords.length} infractions</p>
                    </div>
                    {is_hod || is_head_of_studies || is_principal ? (
                        <button
                            type="button"
                            onClick={() => {
                                setActiveTab('demerits');
                                setFilterStatus('escalated');
                                setShowFilters(true);
                            }}
                            className={`text-left bg-white rounded-2xl border p-5 shadow-sm transition-all hover:shadow-md cursor-pointer relative group ${
                                activeTab === 'demerits' && filterStatus === 'escalated'
                                    ? 'border-purple-500 ring-2 ring-purple-500/20 bg-purple-50/30'
                                    : 'border-purple-100/80 hover:border-purple-300'
                            }`}
                            title="Click to filter escalated cases under Demerits"
                        >
                            <div className="flex items-center justify-between">
                                <p className="text-[10px] font-black uppercase tracking-widest text-purple-700">
                                    Escalated Cases
                                </p>
                                {escalatedOrHodCount > 0 && (
                                    <span className="relative flex h-2.5 w-2.5">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500"></span>
                                    </span>
                                )}
                            </div>
                            <div className="flex items-baseline gap-2 mt-1">
                                <p className="text-2xl font-black text-purple-900">{escalatedOrHodCount}</p>
                                <span className="text-[10px] font-black uppercase tracking-wider text-purple-600/90">
                                    Requiring Action
                                </span>
                            </div>
                            <p className="text-[10px] font-bold text-purple-600/80 mt-1">
                                Click to filter demerits
                            </p>
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => {
                                setActiveTab('demerits');
                                setFilterStatus('pending_review');
                                setShowFilters(true);
                            }}
                            className={`text-left bg-white rounded-2xl border p-5 shadow-sm transition-all hover:shadow-md cursor-pointer relative group ${
                                activeTab === 'demerits' && filterStatus === 'pending_review'
                                    ? 'border-amber-500 ring-2 ring-amber-500/20 bg-amber-50/30'
                                    : 'border-amber-100/80 hover:border-amber-300'
                            }`}
                            title="Click to view pending review incidents under Demerits"
                        >
                            <div className="flex items-center justify-between">
                                <p className="text-[10px] font-black uppercase tracking-widest text-amber-700">
                                    Pending Review Incidents
                                </p>
                                {pendingReviewCount > 0 && (
                                    <span className="relative flex h-2.5 w-2.5">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
                                    </span>
                                )}
                            </div>
                            <div className="flex items-baseline gap-2 mt-1">
                                <p className="text-2xl font-black text-amber-900">{pendingReviewCount}</p>
                                <span className="text-[10px] font-black uppercase tracking-wider text-amber-600/90">
                                    Awaiting Action
                                </span>
                            </div>
                            <p className="text-[10px] font-bold text-amber-600/80 mt-1">
                                Click to filter demerits
                            </p>
                        </button>
                    )}
                </div>

                {/* Underline Tabs Centered */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-gray-200 mb-4 gap-4">
                    <div className="flex-1 flex justify-center">
                        <nav className="flex space-x-10 justify-center" aria-label="Character Registry Tabs">
                            <button
                                type="button"
                                onClick={() => setActiveTab('merits')}
                                className={`pb-3 px-3 text-xs font-black uppercase tracking-widest transition-all relative flex items-center gap-2 border-b-2 ${
                                    activeTab === 'merits'
                                        ? 'text-[#18216D] border-[#18216D]'
                                        : 'text-slate-400 hover:text-slate-600 border-transparent'
                                }`}
                            >
                                <TrophyIcon className={`w-4 h-4 ${activeTab === 'merits' ? 'text-amber-500' : 'text-slate-400'}`} />
                                <span>Merits ({filteredMeritRecords.length})</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveTab('demerits')}
                                className={`pb-3 px-3 text-xs font-black uppercase tracking-widest transition-all relative flex items-center gap-2 border-b-2 ${
                                    activeTab === 'demerits'
                                        ? 'text-[#18216D] border-[#18216D]'
                                        : 'text-slate-400 hover:text-slate-600 border-transparent'
                                }`}
                            >
                                <ExclamationTriangleIcon className={`w-4 h-4 ${activeTab === 'demerits' ? 'text-rose-500' : 'text-slate-400'}`} />
                                <span>Demerits ({filteredDemeritRecords.length})</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveTab('roster')}
                                className={`pb-3 px-3 text-xs font-black uppercase tracking-widest transition-all relative flex items-center gap-2 border-b-2 ${
                                    activeTab === 'roster'
                                        ? 'text-[#18216D] border-[#18216D]'
                                        : 'text-slate-400 hover:text-slate-600 border-transparent'
                                }`}
                            >
                                <ScaleIcon className={`w-4 h-4 ${activeTab === 'roster' ? 'text-[#18216D]' : 'text-slate-400'}`} />
                                <span>Character Roster</span>
                            </button>
                        </nav>
                    </div>

                    {/* Pop-Out Filters Button */}
                    <div className="flex items-center gap-2.5 pb-2 justify-end">
                        <button
                            type="button"
                            onClick={() => setShowFilters(!showFilters)}
                            className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 border transition-all ${
                                showFilters || selectedCourseFilter !== 'all' || selectedYear !== '' || selectedTerm !== '' || (activeTab === 'demerits' && (filterSeverity !== 'all' || filterStatus !== 'all')) || (activeTab === 'roster' && rosterStandingFilter !== 'all')
                                    ? 'bg-[#18216D] text-white border-[#18216D] shadow-sm'
                                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                            }`}
                        >
                            <FunnelIcon className="w-3.5 h-3.5" />
                            <span>Filters</span>
                            {(selectedCourseFilter !== 'all' || selectedYear !== '' || selectedTerm !== '' || (activeTab === 'demerits' && (filterSeverity !== 'all' || filterStatus !== 'all')) || (activeTab === 'roster' && rosterStandingFilter !== 'all')) && (
                                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                            )}
                        </button>
                    </div>
                </div>

                {/* Pop-Out Filters Section Below Tabs & Above Tables */}
                {showFilters && (
                    <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm space-y-4 animate-fadeIn">
                        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                            <div className="flex items-center gap-2">
                                <FunnelIcon className="w-4 h-4 text-[#18216D]" />
                                <span className="text-xs font-black text-[#18216D] uppercase tracking-wider">
                                    Filter Options ({activeTab === 'merits' ? 'Merits' : activeTab === 'demerits' ? 'Demerits' : 'Character Roster'})
                                </span>
                                {(selectedCourseFilter !== 'all' || selectedYear !== '' || selectedTerm !== '' || (activeTab === 'demerits' && (filterSeverity !== 'all' || filterStatus !== 'all')) || (activeTab === 'roster' && rosterStandingFilter !== 'all')) && (
                                    <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-[10px] font-black uppercase tracking-wider">
                                        Filtered
                                    </span>
                                )}
                            </div>
                            {(selectedCourseFilter !== 'all' || selectedYear !== '' || selectedTerm !== '' || filterSeverity !== 'all' || filterStatus !== 'all' || rosterStandingFilter !== 'all') && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSelectedCourseFilter('all');
                                        setSelectedYear('');
                                        setSelectedTerm('');
                                        setFilterSeverity('all');
                                        setFilterStatus('all');
                                        setRosterStandingFilter('all');
                                    }}
                                    className="text-[11px] font-bold text-slate-400 hover:text-rose-600 transition-colors uppercase tracking-wider"
                                >
                                    Reset Filters
                                </button>
                            )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                            {/* Class Scope Filter (Available on all tabs) */}
                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                    Class / Learning Area
                                </label>
                                <FilterDropdown
                                    value={selectedCourseFilter}
                                    onChange={(val) => setSelectedCourseFilter(val || 'all')}
                                    options={courseOptions}
                                    placeholder="All My Classes"
                                    className="w-full"
                                />
                            </div>

                            {/* Academic Year Filter (Available on all tabs) */}
                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                    Academic Year
                                </label>
                                <FilterDropdown
                                    value={selectedYear}
                                    onChange={(val) => {
                                        setSelectedYear(val || '');
                                        setSelectedTerm('');
                                    }}
                                    options={yearOptions}
                                    placeholder="All Academic Years"
                                    className="w-full"
                                />
                            </div>

                            {/* Academic Term Filter (Available on all tabs) */}
                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                    Academic Term
                                </label>
                                <FilterDropdown
                                    value={selectedTerm}
                                    onChange={(val) => setSelectedTerm(val || '')}
                                    options={termOptions}
                                    placeholder="All Terms"
                                    className="w-full"
                                />
                            </div>

                            {/* Severity & Status Filters (for Demerits) */}
                            {activeTab === 'demerits' && (
                                <>
                                    <div>
                                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                            Severity
                                        </label>
                                        <FilterDropdown
                                            value={filterSeverity}
                                            onChange={(val) => setFilterSeverity(val || 'all')}
                                            options={[
                                                { name: 'All Severities', value: 'all' },
                                                { name: 'Minor', value: 'minor' },
                                                { name: 'Moderate', value: 'moderate' },
                                                { name: 'Major', value: 'major' },
                                                { name: 'Severe', value: 'severe' },
                                            ]}
                                            placeholder="All Severities"
                                            className="w-full"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                            Governance Status
                                        </label>
                                        <FilterDropdown
                                            value={filterStatus}
                                            onChange={(val) => setFilterStatus(val || 'all')}
                                            options={[
                                                { name: 'All Statuses', value: 'all' },
                                                { name: 'Logged', value: 'logged' },
                                                { name: 'Pending Review', value: 'pending_review' },
                                                { name: 'Under Action', value: 'under_action' },
                                                { name: 'Resolved', value: 'resolved' },
                                                { name: 'Escalated', value: 'escalated' },
                                            ]}
                                            placeholder="All Statuses"
                                            className="w-full"
                                        />
                                    </div>
                                </>
                            )}

                            {/* Standing Filter (for Character Roster) */}
                            {activeTab === 'roster' && (
                                <div>
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                        Behavioral Standing
                                    </label>
                                    <FilterDropdown
                                        value={rosterStandingFilter}
                                        onChange={(val) => setRosterStandingFilter(val || 'all')}
                                        options={standingOptions}
                                        placeholder="All Standings"
                                        className="w-full"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Tab 1: Merits Feed */}
                {activeTab === 'merits' && (
                    <div className="space-y-4">
                        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                            {loadingRecords ? (
                                <div className="py-20 text-center space-y-3">
                                    <div className="w-10 h-10 border-4 border-[#18216D] border-t-transparent rounded-full animate-spin mx-auto" />
                                    <p className="text-xs font-black uppercase tracking-widest text-slate-400">Loading Merits...</p>
                                </div>
                            ) : filteredMeritRecords.length === 0 ? (
                                <div className="py-20 text-center">
                                    <TrophyIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                                    <h4 className="text-base font-black text-slate-800 uppercase tracking-tight">No Merits Found</h4>
                                    <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
                                        No merit commendations match your current filter selection.
                                    </p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="bg-slate-50/75 border-b border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                                <th className="py-4 px-6">Scholar</th>
                                                <th className="py-4 px-6">Category</th>
                                                <th className="py-4 px-6">Class / Learning Area</th>
                                                <th className="py-4 px-6 text-center">Points</th>
                                                <th className="py-4 px-6">Commendation Notes</th>
                                                <th className="py-4 px-6">Logged By</th>
                                                <th className="py-4 px-6">Date</th>
                                                <th className="py-4 px-6 text-center">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 text-xs">
                                            {filteredMeritRecords.map(record => (
                                                <tr
                                                    key={record.id}
                                                    className="transition-colors hover:bg-emerald-50/30"
                                                >
                                                    {/* Scholar info */}
                                                    <td className="py-4 px-6">
                                                        <div>
                                                            <p className="font-black text-[#18216D] text-xs sm:text-sm">
                                                                {record.student_name}
                                                            </p>
                                                            <p className="text-[10px] font-bold text-slate-400">
                                                                Adm: {record.student_id_number || 'N/A'} {record.student_grade ? `— ${record.student_grade}` : ''}
                                                            </p>
                                                        </div>
                                                    </td>

                                                    {/* Category (No badge!) */}
                                                    <td className="py-4 px-6">
                                                        <div className="space-y-0.5">
                                                            {record.title && (
                                                                <p className="font-black text-[#18216D] text-xs">
                                                                    {record.title}
                                                                </p>
                                                            )}
                                                            <p className={record.title ? "font-medium text-slate-600 text-xs" : "font-black text-slate-800 text-xs"}>
                                                                {record.category_name}
                                                            </p>
                                                        </div>
                                                    </td>

                                                    {/* Course / Learning Area */}
                                                    <td className="py-4 px-6">
                                                        <span className="font-bold text-slate-700">
                                                            {record.learning_area_name || record.course_name || 'General / School-Wide'}
                                                        </span>
                                                    </td>

                                                    {/* Points */}
                                                    <td className="py-4 px-6 text-center">
                                                        <span className="inline-block px-3 py-1 rounded-xl text-xs font-black bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                            +{record.points} pts
                                                        </span>
                                                    </td>

                                                    {/* Commendation Notes */}
                                                    <td className="py-4 px-6 max-w-xs">
                                                        {record.description ? (
                                                            <PremiumTooltip
                                                                text={record.description}
                                                                position="top"
                                                                allowWrap={true}
                                                                maxWidth="max-w-xs sm:max-w-sm"
                                                                wrapperClassName="relative block w-full"
                                                                textClassName="text-xs font-medium text-slate-700 normal-case tracking-normal"
                                                            >
                                                                <p className="text-slate-700 font-medium truncate text-xs cursor-pointer hover:text-[#18216D]">
                                                                    {record.description}
                                                                </p>
                                                            </PremiumTooltip>
                                                        ) : (
                                                            <span className="text-slate-300 text-xs italic">No commendation notes</span>
                                                        )}
                                                    </td>

                                                    {/* Logged By */}
                                                    <td className="py-4 px-6 whitespace-nowrap">
                                                        <div className="flex items-center gap-1.5 text-xs text-slate-700 font-bold">
                                                            <UserCircleIcon className="w-3.5 h-3.5 text-slate-400" />
                                                            <span>{record.logged_by_name || 'Staff'}</span>
                                                        </div>
                                                    </td>

                                                    {/* Standalone Date Column */}
                                                    <td className="py-4 px-6 whitespace-nowrap">
                                                        <div className="flex items-center gap-1.5 text-xs text-slate-600 font-bold">
                                                            <CalendarIcon className="w-3.5 h-3.5 text-slate-400" />
                                                            <span>{formatDate(record.logged_at || record.created_at)}</span>
                                                        </div>
                                                    </td>

                                                    {/* Status: Logged */}
                                                    <td className="py-4 px-6 text-center">
                                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                                            Logged
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Tab 2: Demerits Feed */}
                {activeTab === 'demerits' && (
                    <div className="space-y-4">
                        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                            {loadingRecords ? (
                                <div className="py-20 text-center space-y-3">
                                    <div className="w-10 h-10 border-4 border-[#18216D] border-t-transparent rounded-full animate-spin mx-auto" />
                                    <p className="text-xs font-black uppercase tracking-widest text-slate-400">Loading Demerits...</p>
                                </div>
                            ) : filteredDemeritRecords.length === 0 ? (
                                <div className="py-20 text-center">
                                    <ExclamationTriangleIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                                    <h4 className="text-base font-black text-slate-800 uppercase tracking-tight">No Demerits Found</h4>
                                    <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
                                        No demerit infractions match your current filter selection.
                                    </p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="bg-slate-50/75 border-b border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                                <th className="py-4 px-6">Scholar</th>
                                                <th className="py-4 px-6">Category</th>
                                                <th className="py-4 px-6">Class / Learning Area</th>
                                                <th className="py-4 px-6 text-center">Severity</th>
                                                <th className="py-4 px-6 text-center">Points</th>
                                                <th className="py-4 px-6">Offense Details</th>
                                                <th className="py-4 px-6">Consequences</th>
                                                <th className="py-4 px-6">Logged By</th>
                                                <th className="py-4 px-6">Date</th>
                                                <th className="py-4 px-6">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 text-xs">
                                            {filteredDemeritRecords.map(record => {
                                                const isClassTeacherOfStudent = is_class_teacher && (
                                                    (record.class_teacher_user_id && record.class_teacher_user_id === user?.id) ||
                                                    (coursesData?.assigned_grade || []).some(g => g.stream_id === record.student_stream_id)
                                                );
                                                const isHodOrAdmin = is_hod || is_head_of_studies || is_principal || user?.is_superuser || user?.role === 'ADMIN';
                                                const isAssignedOwner = (record.active_owners_detail || []).some(o => o.id === user?.id) || record.assigned_to_id === user?.id;

                                                // If record.status is 'logged' (minor/moderate), no actions or escalation allowed
                                                const isActionable = ['pending_review', 'under_action', 'escalated'].includes(record.status);
                                                const canAcknowledge = isActionable && (isClassTeacherOfStudent || isHodOrAdmin || isAssignedOwner) && (record.status === 'pending_review' || record.status === 'escalated');
                                                const canEscalate = isActionable && (isClassTeacherOfStudent || isHodOrAdmin) && ['pending_review', 'under_action'].includes(record.status);
                                                const canResolve = isActionable && (isClassTeacherOfStudent || isHodOrAdmin || isAssignedOwner) && ['pending_review', 'under_action', 'escalated'].includes(record.status);
                                                const canVoid = isHodOrAdmin && record.status !== 'voided' && record.status !== 'logged';

                                                const hasAnyAction = canAcknowledge || canEscalate || canResolve || canVoid;

                                                return (
                                                    <tr
                                                        key={record.id}
                                                        className={`transition-colors ${
                                                            record.status === 'escalated'
                                                                ? 'border-l-4 border-l-purple-600 bg-purple-50/25 hover:bg-purple-50/40'
                                                                : 'hover:bg-rose-50/20'
                                                        }`}
                                                    >
                                                        {/* Scholar info */}
                                                        <td className="py-4 px-6">
                                                            <div>
                                                                <p className="font-black text-[#18216D] text-xs sm:text-sm">
                                                                    {record.student_name}
                                                                </p>
                                                                <p className="text-[10px] font-bold text-slate-400">
                                                                    Adm: {record.student_id_number || 'N/A'} {record.student_grade ? `— ${record.student_grade}` : ''}
                                                                </p>
                                                            </div>
                                                        </td>

                                                        {/* Category */}
                                                        <td className="py-4 px-6">
                                                            <p className="font-black text-slate-800 text-xs">
                                                                {record.category_name}
                                                            </p>
                                                        </td>

                                                        {/* Course / Learning Area */}
                                                        <td className="py-4 px-6">
                                                            <span className="font-bold text-slate-700">
                                                                {record.learning_area_name || record.course_name || 'General / School-Wide'}
                                                            </span>
                                                        </td>

                                                        {/* Severity */}
                                                        <td className="py-4 px-6 text-center">
                                                            <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${
                                                                record.severity === 'severe'
                                                                    ? 'bg-rose-600 text-white'
                                                                    : record.severity === 'major'
                                                                    ? 'bg-orange-100 text-orange-800'
                                                                    : record.severity === 'moderate'
                                                                    ? 'bg-amber-100 text-amber-800'
                                                                    : 'bg-slate-100 text-slate-700'
                                                            }`}>
                                                                {record.severity}
                                                            </span>
                                                        </td>

                                                        {/* Points */}
                                                        <td className="py-4 px-6 text-center">
                                                            <span className="inline-block px-3 py-1 rounded-xl text-xs font-black bg-rose-50 text-rose-700 border border-rose-200">
                                                                -{record.points} pts
                                                            </span>
                                                        </td>

                                                        {/* Offense Details */}
                                                        <td className="py-4 px-6 max-w-xs">
                                                            {record.description ? (
                                                                <PremiumTooltip
                                                                    text={record.description}
                                                                    position="top"
                                                                    allowWrap={true}
                                                                    maxWidth="max-w-xs sm:max-w-sm"
                                                                    wrapperClassName="relative block w-full"
                                                                    textClassName="text-xs font-medium text-slate-700 normal-case tracking-normal"
                                                                >
                                                                    <p className="text-slate-700 font-medium truncate text-xs cursor-pointer hover:text-[#18216D]">
                                                                        {record.description}
                                                                    </p>
                                                                </PremiumTooltip>
                                                            ) : (
                                                                <span className="text-slate-300 text-xs italic">No description</span>
                                                            )}
                                                        </td>

                                                        {/* Consequences */}
                                                        <td className="py-4 px-6 max-w-xs">
                                                            {record.consequences ? (
                                                                <PremiumTooltip
                                                                    text={record.consequences}
                                                                    position="top"
                                                                    allowWrap={true}
                                                                    maxWidth="max-w-xs sm:max-w-sm"
                                                                    wrapperClassName="relative block w-full"
                                                                    textClassName="text-xs font-medium text-slate-700 normal-case tracking-normal"
                                                                >
                                                                    <p className="text-slate-700 font-medium truncate text-xs cursor-pointer hover:text-[#18216D]">
                                                                        {record.consequences}
                                                                    </p>
                                                                </PremiumTooltip>
                                                            ) : (
                                                                <span className="text-slate-300 text-xs italic">—</span>
                                                            )}
                                                        </td>

                                                        {/* Logged By */}
                                                        <td className="py-4 px-6 whitespace-nowrap">
                                                            <div className="flex items-center gap-1.5 text-xs text-slate-700 font-bold">
                                                                <UserCircleIcon className="w-3.5 h-3.5 text-slate-400" />
                                                                <span>{record.logged_by_name || 'Staff'}</span>
                                                            </div>
                                                        </td>

                                                        {/* Standalone Date Column */}
                                                        <td className="py-4 px-6 whitespace-nowrap">
                                                            <div className="flex items-center gap-1.5 text-xs text-slate-600 font-bold">
                                                                <CalendarIcon className="w-3.5 h-3.5 text-slate-400" />
                                                                <span>{formatDate(record.logged_at || record.created_at)}</span>
                                                            </div>
                                                        </td>

                                                        {/* Status & Inline Action */}
                                                        <td className="py-4 px-6">
                                                            <div className="relative inline-block text-left action-menu-container">
                                                                <div className="space-y-1">
                                                                    <div className="flex items-center gap-1.5">
                                                                        {record.status === 'escalated' ? (
                                                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-purple-100 text-purple-800 border border-purple-300 shadow-sm">
                                                                                <span className="w-1.5 h-1.5 rounded-full bg-purple-600 animate-pulse" />
                                                                                🚨 Escalated to HOD
                                                                            </span>
                                                                        ) : record.status === 'pending_review' ? (
                                                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200">
                                                                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                                                                Pending Review
                                                                            </span>
                                                                        ) : record.status === 'under_action' ? (
                                                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-blue-100 text-blue-800 border border-blue-200">
                                                                                <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                                                                                Under Action
                                                                            </span>
                                                                        ) : record.status === 'resolved' ? (
                                                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200">
                                                                                <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-600" />
                                                                                Resolved
                                                                            </span>
                                                                        ) : record.status === 'voided' ? (
                                                                            <span className="inline-block px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-slate-200 text-slate-500 line-through">
                                                                                Voided
                                                                            </span>
                                                                        ) : (
                                                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-slate-100 text-slate-700">
                                                                                {record.status?.replace('_', ' ')}
                                                                            </span>
                                                                        )}

                                                                        {hasAnyAction && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    setOpenActionMenuId(openActionMenuId === record.id ? null : record.id);
                                                                                }}
                                                                                className="inline-flex items-center gap-1 px-2 py-1 bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 rounded-lg text-[10px] font-black uppercase tracking-wider border border-slate-200 shadow-sm transition-all active:scale-95"
                                                                                title="Update status"
                                                                            >
                                                                                <span>Action</span>
                                                                                <ChevronDownIcon className="w-3 h-3 text-slate-400" />
                                                                            </button>
                                                                        )}
                                                                    </div>

                                                                    {record.active_owners_detail && record.active_owners_detail.length > 0 && (
                                                                        <div className="text-[10px] text-slate-500 font-bold flex items-center gap-1">
                                                                            <UserCircleIcon className="w-3.5 h-3.5 text-slate-400" />
                                                                            <span>{record.active_owners_detail.map(o => o.name).join(', ')}</span>
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* Dropdown Menu directly positioned under Status */}
                                                                {hasAnyAction && openActionMenuId === record.id && (
                                                                    <div className="absolute right-0 mt-1 w-48 bg-white rounded-2xl shadow-xl border border-slate-200 py-1.5 z-40 animate-fadeIn text-left">
                                                                        {/* Acknowledge / Move to Under Action */}
                                                                        {canAcknowledge && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    acknowledgeMutation.mutate(record.id);
                                                                                    setOpenActionMenuId(null);
                                                                                }}
                                                                                disabled={acknowledgeMutation.isPending}
                                                                                className="w-full px-4 py-2 text-xs font-bold text-blue-700 hover:bg-blue-50 flex items-center gap-2 transition-colors"
                                                                            >
                                                                                <ArrowPathIcon className="w-4 h-4 text-blue-600" />
                                                                                <span>Mark Under Action</span>
                                                                            </button>
                                                                        )}

                                                                        {/* Escalate (Exclusively Class Teacher or HOD/Admin) */}
                                                                        {canEscalate && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    setEscalatingRecord(record);
                                                                                    setEscalationOwners([]);
                                                                                    setEscalationNote('');
                                                                                    setOpenActionMenuId(null);
                                                                                }}
                                                                                className="w-full px-4 py-2 text-xs font-bold text-purple-700 hover:bg-purple-50 flex items-center gap-2 transition-colors"
                                                                            >
                                                                                <ShieldExclamationIcon className="w-4 h-4 text-purple-600" />
                                                                                <span>Escalate Case...</span>
                                                                            </button>
                                                                        )}

                                                                        {/* Resolve Case */}
                                                                        {canResolve && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    setResolvingRecord(record);
                                                                                    setResolutionNote('');
                                                                                    setOpenActionMenuId(null);
                                                                                }}
                                                                                className="w-full px-4 py-2 text-xs font-bold text-emerald-700 hover:bg-emerald-50 flex items-center gap-2 transition-colors"
                                                                            >
                                                                                <CheckCircleIcon className="w-4 h-4 text-emerald-600" />
                                                                                <span>Resolve Case...</span>
                                                                            </button>
                                                                        )}

                                                                        {/* Void Record (HOD / Admin only) */}
                                                                        {canVoid && (
                                                                            <div className="border-t border-slate-100 my-1 pt-1">
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => {
                                                                                        setVoidingRecord(record);
                                                                                        setVoidReason('');
                                                                                        setOpenActionMenuId(null);
                                                                                    }}
                                                                                    className="w-full px-4 py-2 text-xs font-bold text-rose-600 hover:bg-rose-50 flex items-center gap-2 transition-colors"
                                                                                >
                                                                                    <XMarkIcon className="w-4 h-4 text-rose-600" />
                                                                                    <span>Void Record...</span>
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Tab 3: Character Roster */}
                {activeTab === 'roster' && (
                    <ScholarStandingRoster
                        scope="teacher"
                        courseId={selectedCourseFilter !== 'all' ? selectedCourseFilter : null}
                        yearId={selectedYear}
                        selectedTermProp={selectedTerm}
                        onTermChangeProp={setSelectedTerm}
                        title="Character Roster"
                        hideStats={true}
                        selectedFilterProp={rosterStandingFilter}
                        hideTermSelector={true}
                    />
                )}

            </div>

            <ClassHouseReportModal isOpen={isReportModalOpen} onClose={() => setIsReportModalOpen(false)} />

            {/* Reusable Log Discipline Modal */}
            <LogDisciplineModal
                isOpen={isLogModalOpen}
                onClose={() => setIsLogModalOpen(false)}
                onSuccess={() => {
                    queryClient.invalidateQueries({ queryKey: ['teacher-discipline-records'] });
                    queryClient.invalidateQueries({ queryKey: ['discipline-roster-standing'] });
                }}
                coursesList={teacherCourses}
                preselectedCourseId={selectedCourseFilter !== 'all' ? selectedCourseFilter : null}
            />

            {/* Escalation Modal */}
            {escalatingRecord && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
                    <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl border border-slate-100 overflow-hidden">
                        <div className="bg-[#18216D] px-8 py-6 text-white flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-black uppercase tracking-tight">
                                    Escalate Disciplinary Incident
                                </h3>
                                <p className="text-indigo-200 text-xs mt-0.5 font-medium">
                                    Reassign to HOD or pastoral leadership for formal escalation.
                                </p>
                            </div>
                            <button onClick={() => setEscalatingRecord(null)} className="text-white/70 hover:text-white p-2">
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleEscalateSubmit} className="p-8 space-y-5">
                            <div className="bg-rose-50 p-4 rounded-2xl border border-rose-100">
                                <p className="text-xs font-black text-rose-900">{escalatingRecord.student_name}</p>
                                <p className="text-[11px] text-rose-700 mt-0.5">{escalatingRecord.category_name} ({escalatingRecord.severity})</p>
                            </div>

                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
                                    Reassign Follow-up Owner(s) *
                                </label>
                                <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-xl p-2 divide-y divide-slate-50 bg-slate-50">
                                    {staffList.map(st => (
                                        <label key={st.id} className="flex items-center gap-3 p-2 hover:bg-white rounded-lg cursor-pointer transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={escalationOwners.includes(st.id)}
                                                onChange={() => handleEscalationOwnerToggle(st.id)}
                                                className="w-4 h-4 text-[#18216D] rounded border-slate-300"
                                            />
                                            <div className="text-xs">
                                                <span className="font-bold text-slate-800">{st.name}</span>
                                                {st.is_hod && (
                                                    <span className="ml-2 text-[9px] font-black bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded">HOD</span>
                                                )}
                                                {st.is_principal && (
                                                    <span className="ml-2 text-[9px] font-black bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">Principal</span>
                                                )}
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
                                    Reason for Escalation
                                </label>
                                <textarea
                                    rows={3}
                                    value={escalationNote}
                                    onChange={(e) => setEscalationNote(e.target.value)}
                                    placeholder="Explain why this case is being escalated to department leadership..."
                                    className="w-full px-4 py-3 text-xs font-bold bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-[#18216D] outline-none"
                                />
                            </div>

                            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                                <button
                                    type="button"
                                    onClick={() => setEscalatingRecord(null)}
                                    className="px-5 py-2.5 text-xs font-bold text-slate-500 hover:text-slate-800"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={escalateMutation.isPending}
                                    className="px-6 py-2.5 bg-purple-700 hover:bg-purple-800 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all disabled:opacity-50"
                                >
                                    Confirm Escalation
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Resolve Modal */}
            {resolvingRecord && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
                    <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl border border-slate-100 overflow-hidden">
                        <div className="bg-emerald-700 px-8 py-6 text-white flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-black uppercase tracking-tight">
                                    Resolve Disciplinary Case
                                </h3>
                                <p className="text-emerald-100 text-xs mt-0.5 font-medium">
                                    Formally close this case and notify the student's guardian.
                                </p>
                            </div>
                            <button onClick={() => setResolvingRecord(null)} className="text-white/70 hover:text-white p-2">
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleResolveSubmit} className="p-8 space-y-5">
                            <div className="bg-emerald-50 p-4 rounded-2xl border border-emerald-100">
                                <p className="text-xs font-black text-emerald-900">{resolvingRecord.student_name}</p>
                                <p className="text-[11px] text-emerald-700 mt-0.5">{resolvingRecord.category_name} ({resolvingRecord.severity})</p>
                            </div>

                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
                                    Resolution Notes & Actions Taken *
                                </label>
                                <textarea
                                    rows={4}
                                    value={resolutionNote}
                                    onChange={(e) => setResolutionNote(e.target.value)}
                                    required
                                    placeholder="Detail the restorative steps completed, counsel provided, or corrective measures applied..."
                                    className="w-full px-4 py-3 text-xs font-bold bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-emerald-600 outline-none"
                                />
                                <p className="text-[10px] text-slate-400 mt-1 italic">
                                    These notes will be recorded in the student's disciplinary history and made accessible to parents.
                                </p>
                            </div>

                            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                                <button
                                    type="button"
                                    onClick={() => setResolvingRecord(null)}
                                    className="px-5 py-2.5 text-xs font-bold text-slate-500 hover:text-slate-800"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={resolveMutation.isPending}
                                    className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all disabled:opacity-50"
                                >
                                    {resolveMutation.isPending ? 'Resolving...' : 'Confirm Resolution'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Void Modal (HOD & Admin) */}
            {voidingRecord && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
                    <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl border border-slate-100 overflow-hidden">
                        <div className="bg-rose-700 px-8 py-6 text-white flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-black uppercase tracking-tight">
                                    Void Disciplinary Record
                                </h3>
                                <p className="text-rose-100 text-xs mt-0.5 font-medium">
                                    HOD & Senior Administrative Authorization
                                </p>
                            </div>
                            <button onClick={() => setVoidingRecord(null)} className="text-white/70 hover:text-white p-2">
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleVoidSubmit} className="p-8 space-y-5">
                            <div className="bg-rose-50 p-4 rounded-2xl border border-rose-200 text-xs text-rose-800 space-y-1">
                                <p className="font-black">Caution: Permanent Exclusion</p>
                                <p className="text-[11px] leading-relaxed">
                                    Voiding will exclude this record from all merit/demerit standings, point tallies, and student conduct averages. An immutable audit entry will be created.
                                </p>
                            </div>

                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
                                    Mandatory Reason for Voiding *
                                </label>
                                <textarea
                                    rows={3}
                                    value={voidReason}
                                    onChange={(e) => setVoidReason(e.target.value)}
                                    required
                                    placeholder="Provide justification (e.g., recorded in error, duplicate entry, formal appeal upheld)..."
                                    className="w-full px-4 py-3 text-xs font-bold bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-rose-600 outline-none"
                                />
                            </div>

                            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                                <button
                                    type="button"
                                    onClick={() => setVoidingRecord(null)}
                                    className="px-5 py-2.5 text-xs font-bold text-slate-500 hover:text-slate-800"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={voidMutation.isPending}
                                    className="px-6 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all disabled:opacity-50"
                                >
                                    {voidMutation.isPending ? 'Voiding...' : 'Void Record'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeacherDisciplineManager;
